import { useEffect, useRef, useState } from 'react';

import { MAX_PAGE_LIMIT, type ClauseListResponse, type LibraryClause } from '@clm/shared-types';

import { fetchClauses } from '../../api/clauses';
import { ApiError } from '../../api/errors';
import { classifyApiError } from '../../api/problem';

/**
 * The Correct action's clause picker (CLM-60, D-60-3 / D-60-4).
 *
 * A corrected match names a **library** clause, never a free-text code
 * (FR-14 spirit): the reviewer picks from `GET /v1/clauses`. That endpoint has
 * no `q` parameter (`include_inactive`, `category`, `page` only), so the picker
 * fetches the current library a page at a time — `MAX_PAGE_LIMIT` per page,
 * `Load more clauses` for the rest — and narrows *that fetched list* by code or
 * title in the browser. This is a picker over a list the reviewer may already
 * read (FR-17: all authorised may read the library); it is not a filter on the
 * review queue, which is never filtered client-side.
 *
 * Retired clauses are not offered: the server default (`include_inactive`
 * unset) hides them, and a correction onto a retired clause would be a 404
 * from the API's R2 lookup.
 */

type PickerState =
  | { readonly kind: 'loading'; readonly clauses: readonly LibraryClause[] }
  | { readonly kind: 'ready'; readonly clauses: readonly LibraryClause[]; readonly next_cursor: string | null }
  | { readonly kind: 'problem'; readonly clauses: readonly LibraryClause[]; readonly detail: string };

function narrow(clauses: readonly LibraryClause[], needle: string): readonly LibraryClause[] {
  const wanted = needle.trim().toLowerCase();
  if (wanted === '') return clauses;
  return clauses.filter(
    (clause) => clause.clause_code.toLowerCase().includes(wanted) || clause.title.toLowerCase().includes(wanted),
  );
}

export function ClausePicker({
  picked,
  onPick,
  disabled = false,
}: {
  readonly picked: LibraryClause | null;
  readonly onPick: (clause: LibraryClause) => void;
  readonly disabled?: boolean;
}) {
  const [state, setState] = useState<PickerState>({ kind: 'loading', clauses: [] });
  const [needle, setNeedle] = useState('');
  /** The cursor to load next; `undefined` is the first page. */
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loadNonce, setLoadNonce] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    setState((previous) => ({ kind: 'loading', clauses: previous.clauses }));

    void (async () => {
      try {
        const response: ClauseListResponse = await fetchClauses(
          { page: cursor === undefined ? { limit: MAX_PAGE_LIMIT } : { limit: MAX_PAGE_LIMIT, cursor } },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setState((previous) => ({
          kind: 'ready',
          // Appending, not replacing: the picker is one growing list.
          clauses: cursor === undefined ? response.items : [...previous.clauses, ...response.items],
          next_cursor: response.page.next_cursor,
        }));
      } catch (error) {
        if (controller.signal.aborted) return;
        const kind = classifyApiError(error);
        setState((previous) => ({
          kind: 'problem',
          clauses: previous.clauses,
          detail:
            kind === 'forbidden'
              ? 'The clause library could not be read with your role.'
              : error instanceof ApiError
                ? error.message
                : 'The clause library did not load.',
        }));
      }
    })();

    return () => {
      controller.abort();
    };
  }, [cursor, loadNonce]);

  const shown = narrow(state.clauses, needle);

  return (
    <div className="mr-picker">
      <label className="check">
        Find clause
        <input
          type="search"
          aria-label="Find clause"
          placeholder="Code or title"
          value={needle}
          disabled={disabled}
          onChange={(event) => {
            setNeedle(event.target.value);
          }}
        />
      </label>

      {state.kind === 'problem' ? (
        <p className="mr-ferr" role="alert">
          {state.detail}{' '}
          <button
            type="button"
            className="btn sm"
            disabled={disabled}
            onClick={() => {
              setLoadNonce((n) => n + 1);
            }}
          >
            Retry
          </button>
        </p>
      ) : null}

      {state.kind === 'loading' && state.clauses.length === 0 ? (
        <p className="mut" role="status" aria-busy="true">
          Reading the clause library…
        </p>
      ) : shown.length === 0 && state.kind !== 'loading' ? (
        <p className="mut">
          {state.clauses.length === 0
            ? 'The library has no active clauses to pick from.'
            : 'No fetched clause matches that code or title.'}
        </p>
      ) : (
        <ul className="mr-picker-list" aria-label="Library clauses">
          {shown.map((clause) => (
            <li key={clause.clause_id}>
              <button
                type="button"
                disabled={disabled}
                aria-pressed={picked?.clause_code === clause.clause_code}
                aria-label={`Pick ${clause.clause_code} ${clause.title}`}
                onClick={() => {
                  onPick(clause);
                }}
              >
                <code>{clause.clause_code}</code> v{clause.version_no} · {clause.title}
                {clause.is_active ? null : <span className="inactive"> · retired</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {state.kind === 'ready' && state.next_cursor !== null ? (
        <button
          type="button"
          className="btn sm"
          disabled={disabled}
          onClick={() => {
            setCursor(state.next_cursor ?? undefined);
          }}
        >
          Load more clauses
        </button>
      ) : null}
    </div>
  );
}
